import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Valid status transitions
const VALID_TRANSITIONS = {
  "New Lead": ["Uncontacted", "Qualified Lead", "Nurture", "Dead"],
  "Uncontacted": ["New Lead", "Qualified Lead", "Awaited Visit", "Nurture", "Dead"],
  "Qualified Lead": ["Virtual Meet", "Awaited Visit", "Awaited LOI", "Nurture", "Dead"],
  "Virtual Meet": ["Awaited Visit", "Nurture", "Dead"],
  "Awaited Visit": ["Awaited LOI", "Nurture", "Dead"],
  "Awaited LOI": ["Closed LOI", "Nurture", "Dead"],
  "Closed LOI": ["Outlet Opened", "Dead"],
  "Outlet Opened": [],
  "Nurture": ["New Lead", "Qualified Lead", "Dead"],
  "Dead": ["New Lead", "Nurture"]
};

// Dialog configurations for each status transition
const DIALOG_CONFIGS = {
  "New Lead->Uncontacted": {
    title: "Reason for 'Uncontacted'",
    description: "Please provide the call outcome",
    fields: [
      {
        name: "callOutcome",
        label: "Call Outcome",
        type: "select",
        required: true,
        options: ["Not Received", "Busy", "Call Later", "Cut the Call"]
      },
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        required: false
      }
    ]
  },
  "New Lead->Qualified Lead": {
    title: "Qualify Lead",
    description: "Enter qualification details for this lead",
    fields: [
      {
        name: "interestLevel",
        label: "Interest Level",
        type: "select",
        required: true,
        options: ["High", "Mid", "Low"]
      },
      {
        name: "urgency",
        label: "Urgency",
        type: "select",
        required: true,
        options: ["High", "Mid", "Low"]
      },
      {
        name: "budget",
        label: "Budget",
        type: "text",
        required: true
      },
      {
        name: "financialArrangement",
        label: "Financial Arrangement",
        type: "checkbox",
        required: true,
        options: ["Own Fund", "Loan", "Partner"]
      },
      {
        name: "profileType",
        label: "Profile Type",
        type: "checkbox",
        required: true,
        options: ["Job", "Business"]
      },
      {
        name: "decisionMaker",
        label: "Decision Maker",
        type: "select",
        required: true,
        options: ["Self", "Partner", "Family", "Other"]
      },
      {
        name: "timelineToStart",
        label: "Timeline to Start",
        type: "select",
        required: true,
        options: ["Week", "Month", "Year"]
      },
      {
        name: "commercialProperty",
        label: "Commercial Property Status",
        type: "select",
        required: true,
        options: ["Owned", "Searching", "NotReady"],
        optionLabels: {Owned: "Owned / Ready", Searching: "Searching", NotReady: "Not Ready"}
      },
      {
        name: "additionalContactName",
        label: "Additional Contact Name",
        type: "text",
        required: false
      },
      {
        name: "additionalContactNumber",
        label: "Additional Contact Number",
        type: "text",
        required: false
      },
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        required: false
      }
    ]
  },
  // Uncontacted -> Qualified Lead uses same qualification form
  "Uncontacted->Qualified Lead": {
    title: "Qualify Lead",
    description: "Enter qualification details for this lead",
    fields: [
      {
        name: "interestLevel",
        label: "Interest Level",
        type: "select",
        required: true,
        options: ["High", "Mid", "Low"]
      },
      {
        name: "urgency",
        label: "Urgency",
        type: "select",
        required: true,
        options: ["High", "Mid", "Low"]
      },
      {
        name: "budget",
        label: "Budget",
        type: "text",
        required: true
      },
      {
        name: "financialArrangement",
        label: "Financial Arrangement",
        type: "checkbox",
        required: true,
        options: ["Own Fund", "Loan", "Partner"]
      },
      {
        name: "profileType",
        label: "Profile Type",
        type: "checkbox",
        required: true,
        options: ["Job", "Business"]
      },
      {
        name: "decisionMaker",
        label: "Decision Maker",
        type: "select",
        required: true,
        options: ["Self", "Partner", "Family", "Other"]
      },
      {
        name: "timelineToStart",
        label: "Timeline to Start",
        type: "select",
        required: true,
        options: ["Week", "Month", "Year"]
      },
      {
        name: "commercialProperty",
        label: "Commercial Property Status",
        type: "select",
        required: true,
        options: ["Owned", "Searching", "NotReady"],
        optionLabels: {Owned: "Owned / Ready", Searching: "Searching", NotReady: "Not Ready"}
      },
      {
        name: "additionalContactName",
        label: "Additional Contact Name",
        type: "text",
        required: false
      },
      {
        name: "additionalContactNumber",
        label: "Additional Contact Number",
        type: "text",
        required: false
      },
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        required: false
      }
    ]
  },
  "Qualified Lead->Virtual Meet": {
    title: "Schedule Virtual Meeting",
    description: "Schedule a meeting with the lead. Choose between Google Meet or LMS Virtual Meet.",
    fields: [
      {
        name: "meetingMode",
        label: "Meeting Mode",
        type: "select",
        required: true,
        options: ["Google Meet", "LMS Virtual Meet"],
        defaultValue: "Google Meet"
      },
      {
        name: "meetingDateTime",
        label: "Meeting Date & Time",
        type: "datetime-local",
        required: true
      },
      {
        name: "googleMeetLink",
        label: "Google Meet Link",
        type: "url",
        required: false,
        placeholder: "https://meet.google.com/xxx-xxxx-xxx",
        conditionalOn: { field: "meetingMode", value: "Google Meet" }
      },
      {
        name: "lmsMeetLink",
        label: "LMS Meeting Link",
        type: "url",
        required: false,
        placeholder: "Paste link from LMS Virtual Meet page (e.g., /meet/abc123...)",
        conditionalOn: { field: "meetingMode", value: "LMS Virtual Meet" },
        description: "Create a meeting link from 'LMS Virtual Meet' in sidebar, then paste here"
      },
      {
        name: "messageTemplate",
        label: "Message Template",
        type: "textarea",
        required: true,
        defaultValue: "Dear {leadName},\n\nThank you for your interest in Servall. We have scheduled a virtual meeting with you.\n\n📅 Date & Time: {meetingDateTime}\n🔗 Meeting Link: {meetingLink}\n\nPlease join on time. Looking forward to speaking with you.\n\nBest regards,\n{executiveName}"
      },
      {
        name: "internalRemarks",
        label: "Internal Remarks",
        type: "textarea",
        required: false,
        placeholder: "Add any internal notes about this meeting..."
      }
    ]
  },
  "Virtual Meet->Awaited Visit": {
    title: "Post-Meeting Details",
    description: "Record details from the virtual meeting",
    fields: [
      {
        name: "timeTaken",
        label: "Time Taken for Google Meet",
        type: "select",
        required: true,
        options: ["15 min", "30 min", "45 min", "1 hour+"]
      },
      {
        name: "whoJoined",
        label: "Who Joined the Google Meet",
        type: "text",
        required: true,
        placeholder: "Names of attendees"
      },
      {
        name: "majorQuestions",
        label: "Major Questions Asked by the Lead",
        type: "textarea",
        required: true
      },
      {
        name: "whoModerated",
        label: "Who Moderated the Meet",
        type: "select",
        required: true,
        options: [
          { value: "ML", label: "ML – Mihir Ladhawala" },
          { value: "PI", label: "PI – Pratik" },
          { value: "KN", label: "KN – Kaushal Naik" }
        ]
      },
      {
        name: "estimatedVisitTime",
        label: "Estimated Time for Physical Visit",
        type: "datetime-local",
        required: true
      }
    ]
  },
  "Awaited Visit->Awaited LOI": {
    title: "LOI Details",
    description: "Record details from the physical visit",
    fields: [
      {
        name: "whoSatToClose",
        label: "Who Sat to Close",
        type: "select",
        required: true,
        options: [
          { value: "ML", label: "ML – Mihir Ladhawala" },
          { value: "PI", label: "PI – Pratik" },
          { value: "KN", label: "KN – Kaushal Naik" }
        ]
      },
      {
        name: "modelsDiscussed",
        label: "Models Discussed",
        type: "checkbox",
        required: true,
        options: ["Performax Model", "Buddy Model", "FOFO Model", "FOCO Model", "Franchise Model", "Other"]
      },
      {
        name: "majorQuestions",
        label: "Major Questions Asked by Lead",
        type: "textarea",
        required: true
      },
      {
        name: "expectedTimeline",
        label: "Expected Timeline for LOI",
        type: "text",
        required: true,
        placeholder: "e.g., Within 1 week"
      }
    ]
  },
  "Awaited LOI->Closed LOI": {
    title: "LOI Closure",
    description: "Confirm LOI closure",
    fields: [
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        required: false
      }
    ]
  }
};

const StatusChangeDialog = ({ open, onClose, lead, targetStatus, onSuccess }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const currentStatus = lead?.status || "New Lead";
  const transitionKey = `${currentStatus}->${targetStatus}`;
  const config = DIALOG_CONFIGS[transitionKey];

  useEffect(() => {
    if (open && config) {
      // Initialize form data with defaults
      const initialData = {};
      config.fields.forEach(field => {
        if (field.type === 'checkbox') {
          initialData[field.name] = [];
        } else if (field.defaultValue) {
          initialData[field.name] = field.defaultValue;
        } else {
          initialData[field.name] = '';
        }
      });
      setFormData(initialData);
      setErrors({});
    }
  }, [open, config]);

  const validateForm = () => {
    if (!config) return true;
    
    const newErrors = {};
    config.fields.forEach(field => {
      if (field.required) {
        const value = formData[field.name];
        if (field.type === 'checkbox') {
          if (!value || value.length === 0) {
            newErrors[field.name] = `${field.label} is required`;
          }
        } else if (!value || value.trim() === '') {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
      // URL validation
      if (field.type === 'url' && formData[field.name]) {
        const url = formData[field.name];
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          newErrors[field.name] = 'Must be a valid URL';
        }
      }
    });
    
    // Special validation for Virtual Meet - link required for both modes
    if (transitionKey === "Qualified Lead->Virtual Meet") {
      if (formData.meetingMode === "Google Meet" && !formData.googleMeetLink?.trim()) {
        newErrors.googleMeetLink = "Google Meet link is required";
      }
      if (formData.meetingMode === "LMS Virtual Meet" && !formData.lmsMeetLink?.trim()) {
        newErrors.lmsMeetLink = "LMS Meeting link is required. Create one from 'LMS Virtual Meet' page in sidebar.";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      // Special handling for Virtual Meet - create meeting record with provided link
      if (transitionKey === "Qualified Lead->Virtual Meet") {
        const meetingMode = formData.meetingMode === "Google Meet" ? "google_meet" : "lms_virtual_meet";
        
        // For LMS Virtual Meet, extract roomId from the pasted link
        let roomId = null;
        let meetingLink = null;
        
        if (meetingMode === "lms_virtual_meet") {
          // Extract roomId from link like "/meet/abc123" or "https://domain.com/meet/abc123"
          const lmsLink = formData.lmsMeetLink?.trim() || '';
          const roomMatch = lmsLink.match(/\/meet\/([A-Za-z0-9_-]+)/);
          if (roomMatch) {
            roomId = roomMatch[1];
            meetingLink = `/meet/${roomId}`;
          } else {
            toast.error('Invalid LMS meeting link format. Please paste a valid link from LMS Virtual Meet page.');
            setLoading(false);
            return;
          }
        }
        
        // Create meeting record
        const meetingResponse = await api.post('/meetings', {
          leadId: lead.id,
          meetingMode: meetingMode,
          meetingDateTime: new Date(formData.meetingDateTime).toISOString(),
          googleMeetLink: meetingMode === "google_meet" ? formData.googleMeetLink : null,
          lmsMeetLink: meetingMode === "lms_virtual_meet" ? meetingLink : null,
          roomId: roomId,
          messageTemplate: formData.messageTemplate,
          internalRemarks: formData.internalRemarks
        });
        
        // Update form data with meeting link for message template
        const fullMeetingLink = meetingMode === "google_meet" 
          ? formData.googleMeetLink 
          : `${window.location.origin}${meetingLink}`;
        
        formData.meetingId = meetingResponse.data.id;
        formData.generatedMeetingLink = fullMeetingLink;
      }
      
      const statusResp = await api.post(`/leads/${lead.id}/status`, {
        status: targetStatus,
        statusData: formData
      });
      toast.success(`Status changed to ${targetStatus}`);
      onSuccess(statusResp.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to change status'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (name, option, checked) => {
    setFormData(prev => {
      const current = prev[name] || [];
      if (checked) {
        return { ...prev, [name]: [...current, option] };
      } else {
        return { ...prev, [name]: current.filter(o => o !== option) };
      }
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Check if a conditional field should be shown
  const shouldShowField = (field) => {
    if (!field.conditionalOn) return true;
    const { field: condField, value: condValue } = field.conditionalOn;
    return formData[condField] === condValue;
  };

  const renderField = (field) => {
    // Skip field if conditional check fails
    if (!shouldShowField(field)) return null;
    
    const error = errors[field.name];

    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Select
              value={formData[field.name] || ''}
              onValueChange={(v) => handleInputChange(field.name, v)}
            >
              <SelectTrigger className={error ? 'border-red-500' : ''} data-testid={`field-${field.name}`}>
                <SelectValue placeholder={`Select ${field.label}`} />
              </SelectTrigger>
              <SelectContent>
                {field.options.map((opt) => {
                  const value = typeof opt === 'object' ? opt.value : opt;
                  const fallback = typeof opt === 'object' ? opt.label : opt;
                  const label = (field.optionLabels && field.optionLabels[value]) || fallback;
                  return (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <div className="flex flex-wrap gap-4">
              {field.options.map((opt) => (
                <div key={opt} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${field.name}-${opt}`}
                    checked={(formData[field.name] || []).includes(opt)}
                    onCheckedChange={(checked) => handleCheckboxChange(field.name, opt, checked)}
                    data-testid={`field-${field.name}-${opt}`}
                  />
                  <label htmlFor={`${field.name}-${opt}`} className="text-sm">{opt}</label>
                </div>
              ))}
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Textarea
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
              rows={3}
              data-testid={`field-${field.name}`}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'datetime-local':
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <div className="flex gap-2">
              <Input
                type="date"
                value={formData[field.name]?.split('T')[0] || ''}
                onChange={(e) => {
                  const date = e.target.value;
                  const time = formData[field.name]?.split('T')[1] || '10:00';
                  handleInputChange(field.name, date && time ? `${date}T${time}` : '');
                }}
                className={`flex-1 ${error ? 'border-red-500' : ''}`}
                data-testid={`field-${field.name}-date`}
                min={new Date().toISOString().split('T')[0]}
              />
              <Input
                type="time"
                value={formData[field.name]?.split('T')[1] || ''}
                onChange={(e) => {
                  const date = formData[field.name]?.split('T')[0] || new Date().toISOString().split('T')[0];
                  const time = e.target.value;
                  handleInputChange(field.name, date && time ? `${date}T${time}` : '');
                }}
                className={`w-32 ${error ? 'border-red-500' : ''}`}
                data-testid={`field-${field.name}-time`}
              />
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'url':
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Input
              type="text"
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
              data-testid={`field-${field.name}`}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );

      case 'readonly':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <div className="p-3 bg-muted rounded-md border">
              <p className="text-sm text-muted-foreground">
                {field.description || formData[field.name] || 'Will be auto-generated'}
              </p>
              {formData[field.name] && (
                <div className="mt-2 p-2 bg-background rounded border font-mono text-sm break-all">
                  {formData[field.name]}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div key={field.name} className="space-y-2">
            <Label className={field.required ? "after:content-['*'] after:ml-0.5 after:text-red-500" : ""}>
              {field.label}
            </Label>
            <Input
              type="text"
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={error ? 'border-red-500' : ''}
              data-testid={`field-${field.name}`}
            />
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        );
    }
  };

  // Check if transition is valid
  const isValidTransition = VALID_TRANSITIONS[currentStatus]?.includes(targetStatus);

  // State for Dead lead remarks
  const [deadRemarks, setDeadRemarks] = React.useState('');
  const [deadRemarksError, setDeadRemarksError] = React.useState('');

  // If moving to Dead, show form with mandatory remarks
  if (targetStatus === "Dead") {
    const handleDeadSubmit = async () => {
      if (!deadRemarks.trim()) {
        setDeadRemarksError('Please provide a reason for marking this lead as dead');
        return;
      }
      if (deadRemarks.trim().length < 10) {
        setDeadRemarksError('Please provide a detailed reason (minimum 10 characters)');
        return;
      }
      
      setLoading(true);
      try {
        const deadResp = await api.post(`/leads/${lead.id}/status`, {
          status: targetStatus,
          statusData: { deadReason: deadRemarks.trim() }
        });
        toast.success('Lead marked as Dead');
        onSuccess(deadResp.data);
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to change status'));
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Mark Lead as Dead
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for marking this lead as dead. This information is mandatory for tracking purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deadRemarks" className="text-sm font-medium">
                Reason for marking as Dead <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="deadRemarks"
                value={deadRemarks}
                onChange={(e) => {
                  setDeadRemarks(e.target.value);
                  setDeadRemarksError('');
                }}
                placeholder="Enter the reason why this lead is being marked as dead (e.g., Not interested, Budget constraints, Wrong number, etc.)"
                rows={4}
                className={deadRemarksError ? 'border-red-500' : ''}
                data-testid="dead-remarks-input"
              />
              {deadRemarksError && (
                <p className="text-sm text-red-500">{deadRemarksError}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeadSubmit} 
              disabled={loading} 
              data-testid="confirm-dead-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark as Dead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // If no dialog config exists for this transition, show simple confirmation
  if (!config) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              Change status from "{currentStatus}" to "{targetStatus}"?
            </DialogDescription>
          </DialogHeader>
          {!isValidTransition && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <strong>Invalid transition:</strong> Cannot move from "{currentStatus}" to "{targetStatus}".
              Allowed transitions: {VALID_TRANSITIONS[currentStatus]?.join(', ') || 'None'}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading || !isValidTransition} data-testid="confirm-status-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {config.fields.map(renderField)}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="submit-status-change">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Change Status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusChangeDialog;
